import { firebaseAuth } from "../firebase";
import { API_BASE } from "../API";
import { FACEBOOK_APP_ID } from "./facebookApiUtils";

// "Markit WhatsApp Signup" Facebook Login for Business configuration (App Dashboard >
// Facebook Login for Business > Configurations): system-user token that never expires
export const WHATSAPP_CONFIG_ID = "1084100807816075";

// Meta deprecates Embedded Signup v2 on 2026-10-15; v4 needs SDK v25.0 or later
const FACEBOOK_SDK_VERSION = "v25.0";

// TEMP(MARK-3954): mirrors the backend's whatsappConnectUtils.ts types. Move to
// MarkitShared (MarkitAPI.WhatsAppApi + typedAPI.whatsappApi) with the next release.
export type WhatsAppTemplateInfo = {
  id: string;
  name: string;
  status: string; // PENDING / APPROVED / REJECTED / PAUSED / DISABLED
  rejectedReason: string;
};

export type WhatsAppConnectionStatus =
  | { connected: false }
  | {
      connected: true;
      tokenError: boolean;
      wabaId: string;
      phoneNumberId: string;
      displayPhoneNumber: string;
      displayName: string;
      template: WhatsAppTemplateInfo | null;
    };

export type WhatsAppSignupResult = {
  code: string;
  wabaId: string;
  phoneNumberId: string;
};

// TEMP(MARK-3954): the whatsappApi endpoints aren't in the shared typedAPI client
// yet, so this calls them with a plain fetch and the user's Firebase ID token (like
// the master dashboard's dashboardApi.ts).
const requestWhatsAppApi = async <T,>(
  method: "GET" | "POST",
  path: string,
  body?: object
): Promise<T> => {
  const token = await firebaseAuth.currentUser?.getIdToken();
  const res = await fetch(API_BASE + "whatsappApi/" + path, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    throw new Error(`WhatsApp request failed (${res.status})`);
  }
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
};

export const getWhatsAppStatus = () =>
  requestWhatsAppApi<WhatsAppConnectionStatus>("GET", "getStatus");

export const completeWhatsAppConnect = (result: WhatsAppSignupResult) =>
  requestWhatsAppApi<WhatsAppConnectionStatus>(
    "POST",
    "completeConnect",
    result
  );

export const resubmitWhatsAppTemplate = () =>
  requestWhatsAppApi<{ template: WhatsAppTemplateInfo | null }>(
    "POST",
    "resubmitTemplate"
  );

export const disconnectWhatsApp = () =>
  requestWhatsAppApi<void>("POST", "disconnect");

type FacebookSdk = {
  init: (params: object) => void;
  login: (
    callback: (response: { authResponse?: { code?: string } }) => void,
    options: object
  ) => void;
};

const getFacebookSdk = () =>
  (window as unknown as { FB?: FacebookSdk }).FB;

let sdkPromise: Promise<void> | undefined;

// Loads Meta's JS SDK once (it isn't loaded anywhere else; the Pixel is a different
// script). Call it before the Connect press, so FB.login can open its popup straight
// from the click without the browser blocking it.
export const loadFacebookSdk = () => {
  if (sdkPromise) {
    return sdkPromise;
  }
  sdkPromise = new Promise<void>((resolve, reject) => {
    if (getFacebookSdk()) {
      resolve();
      return;
    }
    const sdkWindow = window as unknown as { fbAsyncInit?: () => void };
    sdkWindow.fbAsyncInit = () => {
      getFacebookSdk()?.init({
        appId: FACEBOOK_APP_ID,
        autoLogAppEvents: true,
        xfbml: false,
        version: FACEBOOK_SDK_VERSION,
      });
      resolve();
    };
    const script = document.createElement("script");
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.onerror = () => {
      sdkPromise = undefined;
      reject(new Error("Failed to load the Facebook SDK"));
    };
    document.body.appendChild(script);
  });
  return sdkPromise;
};

/**
 * Opens Meta's Embedded Signup popup. FB.login's callback gives the code, and a
 * WA_EMBEDDED_SIGNUP message event gives the WABA and phone number ids; resolves once
 * both arrive. Post the result to the backend right away: the code expires in 30
 * seconds. Rejects if the user cancels or Meta reports an error. Compare the options
 * with what the App Dashboard's Embedded Signup Builder generates.
 */
export const launchWhatsAppSignup = () =>
  new Promise<WhatsAppSignupResult>((resolve, reject) => {
    const sdk = getFacebookSdk();
    if (!sdk || !WHATSAPP_CONFIG_ID) {
      reject(new Error("WhatsApp signup isn't set up"));
      return;
    }

    let code: string | undefined;
    let ids: { wabaId: string; phoneNumberId: string } | undefined;
    let settled = false;

    const finish = (error?: Error) => {
      if (settled) {
        return;
      }
      if (error) {
        settled = true;
        window.removeEventListener("message", onMessage);
        reject(error);
      } else if (code && ids) {
        settled = true;
        window.removeEventListener("message", onMessage);
        resolve({ code, ...ids });
      }
    };

    const onMessage = (event: MessageEvent) => {
      let origin: string;
      try {
        origin = new URL(event.origin).hostname;
      } catch {
        return;
      }
      if (origin !== "facebook.com" && !origin.endsWith(".facebook.com")) {
        return;
      }
      // Meta's field names
      // eslint-disable-next-line camelcase
      type SignupIds = { waba_id?: string; phone_number_id?: string };
      let data: { type?: string; event?: string; data?: SignupIds };
      try {
        data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }
      if (data?.type !== "WA_EMBEDDED_SIGNUP") {
        return;
      }
      if (data.event === "CANCEL") {
        finish(new Error("cancelled"));
      } else if (data.event === "ERROR") {
        finish(new Error("WhatsApp signup error"));
      } else if (data.event?.startsWith("FINISH")) {
        const wabaId = data.data?.waba_id;
        const phoneNumberId = data.data?.phone_number_id;
        if (!wabaId || !phoneNumberId) {
          finish(new Error("WhatsApp signup finished without a number"));
          return;
        }
        ids = { wabaId, phoneNumberId };
        finish();
      }
    };
    window.addEventListener("message", onMessage);

    sdk.login(
      (response) => {
        code = response.authResponse?.code;
        if (!code) {
          // Closing the popup also lands here, without a code
          finish(new Error("cancelled"));
          return;
        }
        finish();
        // The ids usually arrive first; don't wait past the code's 30 seconds
        setTimeout(
          () => finish(new Error("WhatsApp signup finished without ids")),
          10000
        );
      },
      {
        config_id: WHATSAPP_CONFIG_ID,
        response_type: "code",
        override_default_response_type: true,
        extras: { setup: {} },
      }
    );
  });
